import * as signalR from '@microsoft/signalr';
import { auth } from '~/config/auth';

const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

const redactingSignalRLogger: signalR.ILogger = {
  log(logLevel, message) {
    if (logLevel < signalR.LogLevel.Information) return;
    const redacted = message.replace(/access_token=[^&\s"']+/gi, 'access_token=***');
    if (logLevel >= signalR.LogLevel.Error) console.error(redacted);
    else if (logLevel >= signalR.LogLevel.Warning) console.warn(redacted);
  },
};

export class SignalRService {
  private connection: signalR.HubConnection;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private stopped = false;
  private hasConnected = false;
  private reconnectedHandlers: (() => void)[] = [];

  constructor(hubUrl: string) {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          try {
            return (await auth.getAccessToken()) || '';
          } catch {
            return auth.storage.getTokenStorage().access_token || '';
          }
        },
      })
      .withAutomaticReconnect()
      .configureLogging(redactingSignalRLogger)
      .build();

    this.connection.onreconnected(() => {
      this.reconnectAttempts = 0;
      this.fireReconnected();
    });

    this.connection.onclose((error) => {
      console.error('SignalR connection closed.', error);
      if (this.stopped) return;
      void this.handleConnectionError(error);
    });
  }

  onReconnected(handler: () => void): void {
    this.reconnectedHandlers.push(handler);
  }

  private fireReconnected(): void {
    this.reconnectedHandlers.forEach((handler) => {
      try {
        handler();
      } catch (error) {
        console.error('SignalR reconnected handler error:', error);
      }
    });
  }

  async startConnection(): Promise<void> {
    this.stopped = false;
    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      return;
    }
    try {
      await this.connection.start();
      this.reconnectAttempts = 0;
      if (this.hasConnected) {
        this.fireReconnected();
      }
      this.hasConnected = true;
    } catch (err) {
      console.error('Error establishing SignalR connection:', err);
      await this.handleConnectionError(err);
    }
  }

  private async handleConnectionError(error: unknown): Promise<void> {
    if (this.stopped) {
      return;
    }
    if (this.isUnauthorized(error) && !(await this.tryRefreshToken())) {
      console.error('SignalR: unauthorized and token refresh failed; not reconnecting.');
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.stopped) {
      return;
    }
    this.clearReconnect();
    const delay = Math.min(BASE_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY_MS);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      void this.startConnection();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private isUnauthorized(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return message.includes('401') || message.toLowerCase().includes('unauthorized');
  }

  private async tryRefreshToken(): Promise<boolean> {
    try {
      return !!(await auth.getAccessToken());
    } catch {
      return false;
    }
  }

  onReceiveMessage(handler: (message: any) => void): void {
    this.connection.on('ReceiveMessage', handler);
  }

  onChatCreate(handler: (chatId: string) => void): void {
    this.connection.on('ChatCreate', handler);
  }

  onReceiveNote(handler: (note: unknown) => void): void {
    this.connection.on('ReceiveNote', handler);
  }

  onNoteUpdated(handler: (note: unknown) => void): void {
    this.connection.on('NoteUpdated', handler);
  }

  onNoteDeleted(handler: (noteId: string) => void): void {
    this.connection.on('NoteDeleted', handler);
  }

  onChatHumanEngagementChanged(handler: (chatId: string, requested: boolean, requestedAt?: string | null) => void): void {
    this.connection.on('ChatHumanEngagementChanged', handler);
  }

  onChatAssignmentChanged(
    handler: (
      chatId: string,
      assignedPropertyManagerId: string | null,
      assignedByPropertyManagerId: string | null,
      assignedAt?: string | null
    ) => void
  ): void {
    this.connection.on('ChatAssignmentChanged', handler);
  }

  async stopConnection(): Promise<void> {
    this.stopped = true;
    this.clearReconnect();
    this.reconnectAttempts = 0;
    await this.connection.stop();
  }
}
